import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { Card, CardHeader } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { ROUTES } from '../../utils/constants';
import { getApiClient } from '../../api/client';

interface SettingsProps {
  tenant: string;
}

export function Settings({ tenant }: SettingsProps) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // Timeshift state (admin only)
  const [timeshiftDays, setTimeshiftDays] = useState<string>('0');
  const [timeshiftLoading, setTimeshiftLoading] = useState(false);
  const [timeshiftError, setTimeshiftError] = useState<string | null>(null);
  const [timeshiftSuccess, setTimeshiftSuccess] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadTimeshift();
    }
  }, [isAdmin, tenant]);

  const loadTimeshift = async () => {
    try {
      const client = getApiClient(tenant);
      const response = await client.get<{ timeshiftDays: number }>('/tenant/timeshift');
      setTimeshiftDays(String(response.timeshiftDays));
    } catch (err) {
      console.error('Failed to load timeshift:', err);
    }
  };

  const handleTimeshiftSave = async () => {
    setTimeshiftLoading(true);
    setTimeshiftError(null);
    setTimeshiftSuccess(false);

    try {
      const client = getApiClient(tenant);
      await client.put('/tenant/timeshift', { days: Number(timeshiftDays) });
      setTimeshiftSuccess(true);
      setTimeout(() => setTimeshiftSuccess(false), 3000);
    } catch (err) {
      setTimeshiftError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setTimeshiftLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.login(tenant));
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Admin Settings */}
      {isAdmin && (
        <Card>
          <CardHeader
            title="Admin Settings"
            description="Settings only visible to administrators"
          />

          <div className="space-y-4">
            <div className="py-3">
              <div className="mb-2">
                <p className="font-medium text-gray-900">Time Shift (Days)</p>
                <p className="text-sm text-gray-500">
                  Shift the system time for testing. Positive = future, negative = past.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={timeshiftDays}
                  onChange={(e) => setTimeshiftDays(e.target.value)}
                  className="w-32"
                />
                <Button
                  onClick={handleTimeshiftSave}
                  disabled={timeshiftLoading}
                  size="sm"
                >
                  {timeshiftLoading ? 'Saving...' : 'Save'}
                </Button>
                {timeshiftSuccess && (
                  <span className="text-sm text-green-600">Saved!</span>
                )}
                {timeshiftError && (
                  <span className="text-sm text-red-600">{timeshiftError}</span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Settings"
          description="Manage your app preferences"
        />

        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b">
            <div>
              <p className="font-medium text-gray-900">Notifications</p>
              <p className="text-sm text-gray-500">Manage your notification preferences</p>
            </div>
            <Button variant="outline" size="sm" disabled>
              Coming Soon
            </Button>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <div>
              <p className="font-medium text-gray-900">Privacy</p>
              <p className="text-sm text-gray-500">Control your privacy settings</p>
            </div>
            <Button variant="outline" size="sm" disabled>
              Coming Soon
            </Button>
          </div>

          <div className="flex justify-between items-center py-3">
            <div>
              <p className="font-medium text-gray-900">Data Export</p>
              <p className="text-sm text-gray-500">Download your data</p>
            </div>
            <Button variant="outline" size="sm" disabled>
              Coming Soon
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Danger Zone"
          description="Irreversible actions"
        />

        <div className="space-y-4">
          <div className="flex justify-between items-center py-3">
            <div>
              <p className="font-medium text-gray-900">Sign Out</p>
              <p className="text-sm text-gray-500">Sign out of your account on this device</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Sign Out
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
